import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearAuthStorage,
  isAuthenticated as checkAuthenticated,
  isTokenExpired,
} from '../utils/auth';
import { validateBrowserSession, unregisterTab, clearTabSessionMarkers } from '../services/tabSession';
import { broadcastSessionEvent, closeSessionChannel, SESSION_EVENTS } from '../services/sessionSync';
import { setUnauthorizedHandler } from '../services/sessionHandlers';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return validateBrowserSession() && checkAuthenticated();
  });

  const logout = useCallback(
    ({ reason = 'manual', message, broadcast = true, redirect = true } = {}) => {
      unregisterTab();
      clearTabSessionMarkers();
      clearAuthStorage();
      setIsAuthenticated(false);

      if (broadcast) {
        broadcastSessionEvent(SESSION_EVENTS.LOGOUT, { reason, message });
      }

      closeSessionChannel();

      if (redirect) {
        const state =
          reason === 'idle'
            ? {
                sessionExpired: true,
                message:
                  message ||
                  'Your session has expired due to inactivity. Please log in again.',
              }
            : reason === 'manual' && message
              ? { logoutMessage: message }
              : undefined;

        navigate('/login', { replace: true, state });
      }
    },
    [navigate]
  );

  useEffect(() => {
    setUnauthorizedHandler((msg) => {
      logout({
        reason: 'idle',
        message: msg,
        broadcast: true,
      });
    });

    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const login = useCallback(() => {
    if (isTokenExpired()) {
      clearAuthStorage();
      setIsAuthenticated(false);
      return false;
    }
    setIsAuthenticated(true);
    return true;
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      setIsAuthenticated,
      login,
      logout,
    }),
    [isAuthenticated, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/** Optional hook for components outside strict provider requirements during migration. */
export function useAuthOptional() {
  return useContext(AuthContext);
}
